/**
 * Admin V3 — Corporate client creation modal (AV3 A-6)
 *
 * Operator creates a corporate account via POST /enterprises
 * (RBAC: enterprise:create). Reuses the canonical enterprise endpoint verbatim
 * (EnterpriseCreate schema); the backend remains the single source of truth
 * (uniqueness check, pricing-contract sync when base_rate is set). No new
 * business logic, no backend change.
 *
 * Required by the backend schema: name, email, address, contact_name.
 */
import React, { useState } from 'react';
import { Loader2, X, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const inputCls =
  'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100';

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

// Module-scope field (defining it inside the component would remount inputs on
// every keystroke and lose focus).
const Field = ({ label, k, value, onChange, type = 'text', required }) => (
  <div>
    <label className="text-[12px] font-semibold text-slate-600">
      {label}{required && <span className="text-rose-500"> *</span>}
    </label>
    <input type={type} value={value} onChange={onChange} className={inputCls} data-testid={`adminv3-corporate-create-${k}`} />
  </div>
);

const CorporateCreateModal = ({ open, onClose, onDone, locale = DEFAULT_LOCALE }) => {
  const m = useAdminMutation('/enterprises', 'post');
  const { showSuccess, showError } = useToast();

  const [f, setF] = useState({
    name: '', email: '', contact_name: '', phone: '', address: '',
    siret: '', payment_terms: '30', max_monthly_budget: '', base_rate: '', negotiated_discount: '',
  });
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  if (!open) return null;

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const valid = f.name.trim() && emailOk && f.address.trim() && f.contact_name.trim();

  const submit = () => {
    m.mutate(
      {
        name: f.name.trim(),
        email: f.email.trim(),
        contact_name: f.contact_name.trim(),
        address: f.address.trim(),
        phone: f.phone.trim() || null,
        siret: f.siret.trim() || null,
        payment_terms: f.payment_terms === '' ? 30 : Number(f.payment_terms),
        max_monthly_budget: numOrNull(f.max_monthly_budget),
        base_rate: numOrNull(f.base_rate),
        negotiated_discount: numOrNull(f.negotiated_discount),
      },
      {
        onSuccess: () => {
          showSuccess(t('corporate.create.ok', locale));
          if (onDone) onDone();
          onClose();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('corporate.create.error', locale)),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="adminv3-corporate-create-modal">
      <div className="w-[600px] max-w-[94vw] max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[17px] font-semibold text-slate-900">
            <Building2 className="h-4 w-4 text-indigo-500" />{t('corporate.create.title', locale)}
          </h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="adminv3-corporate-create-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('corporate.field.name', locale)}        k="name"         value={f.name}         onChange={set('name')}         required />
          <Field label={t('corporate.field.email', locale)}       k="email"        value={f.email}        onChange={set('email')}        type="email" required />
          <Field label={t('corporate.field.contactName', locale)} k="contact_name" value={f.contact_name} onChange={set('contact_name')} required />
          <Field label={t('corporate.field.phone', locale)}       k="phone"        value={f.phone}        onChange={set('phone')} />
          <div className="md:col-span-2">
            <Field label={t('corporate.field.address', locale)}   k="address"      value={f.address}      onChange={set('address')}      required />
          </div>
          <Field label={t('corporate.field.siret', locale)}        k="siret"              value={f.siret}              onChange={set('siret')} />
          <Field label={t('corporate.field.paymentTerms', locale)} k="payment_terms"      value={f.payment_terms}      onChange={set('payment_terms')}      type="number" />
          <Field label={t('corporate.field.maxBudget', locale)}    k="max_monthly_budget" value={f.max_monthly_budget} onChange={set('max_monthly_budget')} type="number" />
          <Field label={t('corporate.field.baseRate', locale)}     k="base_rate"          value={f.base_rate}          onChange={set('base_rate')}          type="number" />
          <Field label={t('corporate.field.discount', locale)}     k="negotiated_discount" value={f.negotiated_discount} onChange={set('negotiated_discount')} type="number" />
        </div>

        {!valid && (
          <p className="mt-3 text-[12px] text-rose-600" data-testid="adminv3-corporate-create-missing">
            {t('corporate.create.required', locale)}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="adminv3-corporate-create-dismiss">
            {t('corporate.create.cancel', locale)}
          </Button>
          <Button
            variant="default" size="sm" onClick={submit} disabled={!valid || m.loading}
            data-testid="adminv3-corporate-create-confirm"
          >
            {m.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {t('corporate.create.confirm', locale)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CorporateCreateModal;
